import { html } from '../html.js';
import { DEFAULT_PRINT_OPTIONS } from '../print-options.js';
import { t } from '../strings.js';
import { docDate, field, fields, footer, header, ltr } from './parts.js';
import { notesBlock, party, qtyCell, statusBadge, statusLabel, trackingCell } from './record-parts.js';
/**
 * SAL-03's delivery note as a printed document — the paper that travels with
 * the goods and the driver gets signed.
 *
 * A QUANTITY document, by design. It carries no price and no total: the
 * person receiving a pallet checks counts, not money, and a delivery note
 * with prices on it is an invoice the warehouse never meant to issue. Three
 * figures per line — ORDERED on the sales order, DELIVERED on this note, and
 * REMAINING after it — are what let the customer see at the door whether
 * this is the whole order or the first of several. The unit prints beside
 * each (N-14): a line of "20" is only a count once it says 20 of what.
 *
 * Wave 5B adds the two halves of the paper's journey: the LOGISTICS strip
 * under the header (driver, vehicle, departure, the address it went to) and
 * the PROOF-OF-DELIVERY foot (received by / on / signature) — printed values
 * when the note was signed for on screen, ruled blank cells for the receiver
 * to fill by hand when it travels unsigned (SAL-03 §5d P7). A line not on
 * the order (a sample, a replacement) wears an "Extra" tag and its reason;
 * a DIRECT note (no order) prints no order reference and no ordered /
 * remaining figures at all, because nothing was ordered.
 */
export function deliveryNoteHtml(data, locale, company, generatedAt, generatedBy, print = DEFAULT_PRINT_OPTIONS) {
    const tracked = data.lines.some((line) => (line.batchNo !== null && line.batchNo !== '') || line.serialNos.length > 0);
    const direct = data.salesOrderCode === null;
    const vehicle = [data.vehicleNo, data.vehicleType].filter((part) => part !== null && part.trim() !== '');
    const logistics = [
        data.driver === null || data.driver.trim() === '' ? null : field(t('driver', locale), data.driver),
        vehicle.length === 0 ? null : field(t('vehicleNo', locale), ltr(vehicle.join(' · '))),
        data.departedAt === null ? null : field(t('departedAt', locale), docDate(data.departedAt)),
        data.deliveryAddress === null || data.deliveryAddress.trim() === ''
            ? null
            : field(t('deliveryAddress', locale), data.deliveryAddress),
    ].filter((row) => row !== null);
    return html `
    ${header(company, t('deliveryNoteTitle', locale), statusBadge(data.status, locale))}
    ${fields([
        field(t('deliveryNo', locale), ltr(data.code)),
        field(t('invoiceDate', locale), docDate(data.deliveryDate)),
        ...(direct ? [] : [field(t('salesOrderRef', locale), ltr(data.salesOrderCode ?? ''))]),
        field(t('status', locale), statusLabel(data.status, locale)),
        field(t('customerCode', locale), ltr(data.partnerCode)),
        field(t('warehouse', locale), data.warehouseName),
    ])}
    ${party(t('deliverTo', locale), data.partnerName)}
    ${logistics.length === 0 ? null : fields(logistics)}

    <table class="doc-table">
      <thead>
        <tr>
          <th>${t('lineNo', locale)}</th>
          ${print.showItemCode ? html `<th>${t('itemCode', locale)}</th>` : null}
          <th>${t('description', locale)}</th>
          ${direct ? null : html `<th class="amount">${t('qtyOrdered', locale)}</th>`}
          <th class="amount">${t('qtyDelivered', locale)}</th>
          ${direct ? null : html `<th class="amount">${t('qtyRemaining', locale)}</th>`}
          ${tracked ? html `<th>${t('batchNo', locale)} / ${t('serialNos', locale)}</th>` : null}
        </tr>
      </thead>
      <tbody>
        ${data.lines.map((line) => html `
            <tr>
              <td>${ltr(String(line.lineNo))}</td>
              ${print.showItemCode ? html `<td>${line.itemCode === null ? null : ltr(line.itemCode)}</td>` : null}
              <td>
                ${line.description}${line.isExtra
        ? html `<span class="doc-extra">${t('extraLine', locale)}</span>`
        : null}${line.isExtra
        ? html `<br /><small>${t('extraLineReason', locale)}${line.extraReason === null ||
            line.extraReason.trim() === ''
            ? null
            : html ` — ${line.extraReason}`}</small>`
        : null}${line.note === null || line.note.trim() === ''
        ? null
        : html `<br /><small>${line.note}</small>`}
              </td>
              ${direct
        ? null
        : html `<td class="amount">${line.isExtra ? null : qtyCell(line.qtyOrdered, line.unit)}</td>`}
              <td class="amount">${qtyCell(line.qtyDelivered, line.unit)}</td>
              ${direct
        ? null
        : html `<td class="amount">${line.isExtra ? null : qtyCell(line.qtyRemaining, line.unit)}</td>`}
              ${tracked ? html `<td>${trackingCell(line.batchNo, line.serialNos)}</td>` : null}
            </tr>
          `)}
      </tbody>
    </table>

    ${notesBlock(t('notes', locale), data.notes)}
    ${notesBlock(t('receiverNote', locale), data.receiverNote)}
    <section class="doc-sign">
      <div class="cell">
        <div class="label">${t('receivedBy', locale)}</div>
        <div class="value">${data.receivedBy ?? ''}</div>
      </div>
      <div class="cell">
        <div class="label">${t('receivedAt', locale)}</div>
        <div class="value">${data.receivedAt === null ? '' : docDate(data.receivedAt)}</div>
      </div>
      <div class="cell">
        <div class="label">${t('signature', locale)}</div>
        <div class="value"></div>
      </div>
    </section>
    ${footer(locale, generatedAt, company.name, generatedBy)}
  `;
}
//# sourceMappingURL=delivery-note.js.map